import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { HeroCaption } from "@/components/HeroCarousel";

const LINK_PRESETS = [
  { label: "Free trial form", link: "https://www.themathemind.com/intake-form" },
  { label: "Sign in", link: "/login" },
  { label: "Times Tables Arena", link: "/play/arena" },
  { label: "Worksheet Generator", link: "/worksheet-generator" },
  { label: "MathBot", link: "/login?redirect=/ai-tutor" },
  { label: "Email us", link: "mailto:learn@themathemind.com" },
];
const FIELDS = ["headline", "subtext", "button_label", "button_link"];

export const HeroCaptionDialog = ({ slide, headers, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (slide) setForm(Object.fromEntries(FIELDS.map((k) => [k, slide[k] || ""]))); }, [slide]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/hero-slides/${slide.id}/caption`, form, { headers });
      onSaved({ ...slide, ...r.data });
      toast.success("Caption saved");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save caption"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!slide} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="hero-caption-dialog">
        <DialogHeader><DialogTitle>Slide caption</DialogTitle></DialogHeader>
        {slide && (
          <div className="relative rounded-xl overflow-hidden aspect-[16/7] bg-muted" data-testid="hero-caption-preview">
            {slide.type === "video"
              ? <video src={slide.url} className="w-full h-full object-cover" muted autoPlay loop playsInline />
              : <img src={slide.url} alt="" className="w-full h-full object-cover" />}
            <HeroCaption slide={form} onButton={() => {}} />
          </div>
        )}
        <div className="grid gap-3">
          <div><Label>Headline</Label><Input value={form.headline || ""} onChange={set("headline")} maxLength={100} placeholder="Discover the Magic of Numbers" data-testid="caption-headline-input" /></div>
          <div><Label>Sub-text (optional)</Label><Input value={form.subtext || ""} onChange={set("subtext")} maxLength={200} placeholder="Abacus & Vedic Maths for ages 5+" data-testid="caption-subtext-input" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Button label</Label><Input value={form.button_label || ""} onChange={set("button_label")} maxLength={40} placeholder="Book a Free Trial" data-testid="caption-button-label-input" /></div>
            <div><Label>Button link</Label><Input value={form.button_link || ""} onChange={set("button_link")} placeholder="/login or https://…" data-testid="caption-button-link-input" /></div>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="caption-link-presets">
            {LINK_PRESETS.map((p) => (
              <button key={p.link} type="button" onClick={() => setForm((f) => ({ ...f, button_link: p.link }))} className="text-xs px-2.5 py-1 rounded-full border border-border hover:bg-muted transition-colors" data-testid={`caption-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {p.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Leave everything empty for a photo-only slide. Links starting with / open inside the app; https:// links open in a new tab.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setForm(Object.fromEntries(FIELDS.map((k) => [k, ""])))} data-testid="caption-clear-btn">Clear</Button>
          <Button onClick={save} disabled={saving} data-testid="caption-save-btn">{saving ? "Saving..." : "Save caption"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
